"use client";

import { Check } from "lucide-react";
import dynamic from "next/dynamic";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  OnboardingLabel,
  OnboardingPrimaryButton,
} from "@/components/onboarding/onboarding-chrome";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  encodeTicketMediaAnswers,
  MAX_TICKET_ANSWER_LENGTH,
  MAX_TICKET_MEDIA_BYTES,
  readTicketMediaInputs,
  type TicketQuestion,
} from "@/lib/events/ticket-questions";
import { cn } from "@/lib/utils";

const ImageUpload = dynamic(() =>
  import("@/components/image-upload").then((module) => module.ImageUpload),
);

function TicketMediaField({
  value,
  onChange,
  onBusyChange,
  maxFiles,
}: {
  value: string;
  maxFiles: number;
  onChange: (value: string) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const initialImages = useMemo(() => {
    return readTicketMediaInputs(value) ?? [];
  }, [value]);
  return (
    <div className="space-y-2">
      <p className="pl-1 text-sm text-text-secondary">
        {maxFiles === 1
          ? "One photo or video"
          : `Up to ${maxFiles} photos or videos`}{" "}
        · 20 MB each
      </p>
      <ImageUpload
        maxImages={maxFiles}
        initialImages={initialImages}
        acceptVideos
        maxImageSize={MAX_TICKET_MEDIA_BYTES}
        maxVideoSize={MAX_TICKET_MEDIA_BYTES}
        enableCropping={false}
        showCountHeading={false}
        showReorderHint={false}
        uploadScope={{ domain: "events", slot: "ticket-answers" }}
        onUploadStateChange={(state) =>
          onBusyChange(
            Boolean(state.isUploading || state.isReviewing || state.isCropping),
          )
        }
        onImagesChange={(images) => {
          const ready = images.filter(
            (image) => !image.isUploading && !image.error,
          );
          const next = encodeTicketMediaAnswers(ready);
          if (next !== value) onChange(next);
        }}
      />
    </div>
  );
}

/** Same field surface as the booking-details drawer and onboarding step 1. */
const FIELD_CLASS =
  "h-[52px] w-full rounded-2xl border border-border/60 bg-surface/55 px-4 font-semibold text-[17px] text-text-primary backdrop-blur-xl backdrop-saturate-150 transition-[border-color,box-shadow] duration-200 placeholder:font-medium placeholder:text-text-tertiary focus-visible:border-brand/60 focus-visible:ring-4 focus-visible:ring-brand/20 md:text-lg dark:bg-white/5";
const TEXTAREA_CLASS =
  "min-h-[104px] w-full rounded-2xl border border-border/60 bg-surface/55 px-4 py-3 font-medium text-[16px] text-text-primary backdrop-blur-xl backdrop-saturate-150 transition-[border-color,box-shadow] duration-200 placeholder:text-text-tertiary focus-visible:border-brand/60 focus-visible:ring-4 focus-visible:ring-brand/20 dark:bg-white/5";

export type TicketAnswerValues = Record<string, string>;

/** Client mirror of the server rule: only required questions can block. */
export function ticketAnswerErrors(
  questions: TicketQuestion[],
  values: TicketAnswerValues,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const question of questions) {
    const value = values[question.id]?.trim() ?? "";
    if (question.kind === "media" && value) {
      const files = readTicketMediaInputs(value);
      if (!files || files.length > (question.maxFiles ?? 1)) {
        errors[question.id] = `Upload up to ${question.maxFiles ?? 1} files`;
      }
    }
    if (question.required && !value) {
      errors[question.id] =
        question.kind === "choice"
          ? "Choose one"
          : question.kind === "media"
            ? "Upload a file to continue"
            : "Answer this to continue";
    }
  }
  return errors;
}

export function TicketQuestionFields({
  questions,
  values,
  onChange,
  errors,
  idPrefix,
  onUploadingChange,
}: {
  questions: TicketQuestion[];
  values: TicketAnswerValues;
  onChange: (questionId: string, value: string) => void;
  errors?: Record<string, string>;
  idPrefix: string;
  onUploadingChange?: (busy: boolean) => void;
}) {
  const [busyQuestions, setBusyQuestions] = useState<Record<string, boolean>>(
    {},
  );
  const uploading = questions.some(
    (question) => question.kind === "media" && busyQuestions[question.id],
  );
  const onUploadingChangeRef = useRef(onUploadingChange);
  onUploadingChangeRef.current = onUploadingChange;
  useEffect(() => {
    onUploadingChangeRef.current?.(uploading);
  }, [uploading]);
  return (
    <div className="space-y-5">
      {questions.map((question) => {
        const id = `${idPrefix}-${question.id}`;
        const value = values[question.id] ?? "";
        const error = errors?.[question.id];
        return (
          <div key={question.id} className="space-y-2">
            <OnboardingLabel optional={!question.required}>
              <label
                htmlFor={
                  question.kind === "choice" || question.kind === "media"
                    ? undefined
                    : id
                }
              >
                {question.label}
              </label>
            </OnboardingLabel>
            {question.helpText ? (
              <p className="pl-1 text-sm text-text-secondary">
                {question.helpText}
              </p>
            ) : null}
            {question.kind === "media" ? (
              <fieldset aria-label={question.label}>
                <TicketMediaField
                  value={value}
                  maxFiles={question.maxFiles ?? 1}
                  onChange={(next) => onChange(question.id, next)}
                  onBusyChange={(busy) =>
                    setBusyQuestions((current) =>
                      current[question.id] === busy
                        ? current
                        : { ...current, [question.id]: busy },
                    )
                  }
                />
              </fieldset>
            ) : question.kind === "choice" ? (
              <fieldset className="grid grid-cols-2 gap-2">
                <legend className="sr-only">{question.label}</legend>
                {(question.options ?? []).map((option) => {
                  const selected = value === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={selected}
                      onClick={() =>
                        onChange(
                          question.id,
                          selected && !question.required ? "" : option,
                        )
                      }
                      className={cn(
                        "flex min-h-[52px] items-center justify-center gap-2 rounded-2xl border px-3 py-2 text-center text-[15px] font-semibold transition-[transform,border-color,background-color,color] duration-150 ease-out-strong active:scale-95",
                        selected
                          ? "border-transparent bg-brand text-white dark:bg-[#7c4dff]"
                          : "border-border/60 bg-surface/55 text-text-secondary dark:bg-white/5",
                      )}
                    >
                      {selected ? (
                        <Check className="size-4 shrink-0" strokeWidth={3} />
                      ) : null}
                      <span>{option}</span>
                    </button>
                  );
                })}
              </fieldset>
            ) : question.kind === "long_text" ? (
              <Textarea
                id={id}
                value={value}
                maxLength={MAX_TICKET_ANSWER_LENGTH}
                aria-invalid={Boolean(error)}
                onChange={(event) => onChange(question.id, event.target.value)}
                className={TEXTAREA_CLASS}
              />
            ) : (
              <Input
                id={id}
                value={value}
                maxLength={MAX_TICKET_ANSWER_LENGTH}
                aria-invalid={Boolean(error)}
                onChange={(event) => onChange(question.id, event.target.value)}
                className={FIELD_CLASS}
              />
            )}
            <FieldError
              className="pl-1"
              errors={error ? [{ message: error }] : undefined}
            />
          </div>
        );
      })}
    </div>
  );
}

export type TicketQuestionGroup = {
  passTypeId: number;
  name: string;
  questions: TicketQuestion[];
};

/**
 * Asked at checkout, after the booker's own details, for every ticket in the
 * basket that carries questions. Mounted fresh per opening (callers key it).
 */
export function TicketQuestionsDrawer({
  open,
  onOpenChange,
  tickets,
  initialAnswers,
  onComplete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tickets: TicketQuestionGroup[];
  initialAnswers: Record<number, TicketAnswerValues>;
  onComplete: (answers: Record<number, TicketAnswerValues>) => void;
}) {
  const [answers, setAnswers] =
    useState<Record<number, TicketAnswerValues>>(initialAnswers);
  const [submitted, setSubmitted] = useState(false);
  const [uploadingTickets, setUploadingTickets] = useState<
    Record<number, boolean>
  >({});
  const uploading = tickets.some(
    (ticket) => uploadingTickets[ticket.passTypeId],
  );
  const errorsByTicket = Object.fromEntries(
    tickets.map((ticket) => [
      ticket.passTypeId,
      ticketAnswerErrors(ticket.questions, answers[ticket.passTypeId] ?? {}),
    ]),
  );
  const hasErrors = Object.values(errorsByTicket).some(
    (errors) => Object.keys(errors).length > 0,
  );

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="mx-auto max-h-[90dvh] w-full max-w-[540px] overflow-hidden border-border bg-surface data-[vaul-drawer-direction=bottom]:rounded-t-[30px] dark:border-white/10 dark:bg-[#0d0c13]">
        <DrawerHeader className="shrink-0 px-5 pt-3 pb-1 text-left!">
          <DrawerTitle className="text-[26px] leading-[1.15] font-bold tracking-tight text-balance text-text-primary">
            A few questions
          </DrawerTitle>
          <DrawerDescription className="text-text-secondary">
            The organiser asks these for{" "}
            {tickets.length === 1 ? tickets[0]?.name : "your tickets"}.
          </DrawerDescription>
        </DrawerHeader>
        <form
          id="ticket-questions-form"
          className="min-h-0 flex-1 space-y-7 overflow-y-auto px-5 pt-4 pb-5"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitted(true);
            if (hasErrors || uploading) return;
            onComplete(answers);
          }}
        >
          {tickets.map((ticket) => (
            <section key={ticket.passTypeId} className="space-y-4">
              {tickets.length > 1 ? (
                <h3 className="text-base font-semibold text-text-primary">
                  {ticket.name}
                </h3>
              ) : null}
              <TicketQuestionFields
                idPrefix={`ticket-${ticket.passTypeId}`}
                questions={ticket.questions}
                onUploadingChange={(busy) =>
                  setUploadingTickets((current) =>
                    current[ticket.passTypeId] === busy
                      ? current
                      : { ...current, [ticket.passTypeId]: busy },
                  )
                }
                values={answers[ticket.passTypeId] ?? {}}
                errors={
                  submitted ? errorsByTicket[ticket.passTypeId] : undefined
                }
                onChange={(questionId, value) =>
                  setAnswers((current) => ({
                    ...current,
                    [ticket.passTypeId]: {
                      ...current[ticket.passTypeId],
                      [questionId]: value,
                    },
                  }))
                }
              />
            </section>
          ))}
        </form>
        <div className="shrink-0 px-5 pt-3 pb-[max(env(safe-area-inset-bottom),20px)]">
          <div className="flex">
            <OnboardingPrimaryButton
              type="submit"
              form="ticket-questions-form"
              disabled={uploading || (submitted && hasErrors)}
            >
              {uploading ? "Uploading…" : "Continue"}
            </OnboardingPrimaryButton>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
